import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { api } from '../../../lib/api';
import TimetableView from '../../class-management-hub/components/TimetableView';
import { TimetableSlot } from '../../class-management-hub/types';

function normalize(s: string): string {
  return (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

function mapType(tip: string | null | undefined): TimetableSlot['type'] {
  const t = normalize(tip || '');
  if (t.includes('lab')) return 'lab';
  if (t.includes('laborator')) return 'lab';
  if (t.includes('seminar') || t.includes('tutorial')) return 'tutorial';
  return 'lecture';
}

function lessonTypeToSlotType(t: string | null | undefined): TimetableSlot['type'] {
  const norm = normalize(t || '');
  if (norm === 'lab') return 'lab';
  if (norm === 'tutorial') return 'tutorial';
  if (norm === 'lecture') return 'lecture';
  return mapType(t);
}

type TimetableInfo = { id: number; name: string; classroom_id: number; gradebook_config_id: number; period_index: number };
type TimetableEntry = {
  id: number;
  day_of_week: number;
  start_time: string;
  end_time: string;
  subject: string;
  room?: string | null;
  lesson_type?: string | null;
  teacher_name?: string | null;
};

function entryToSlot(entry: TimetableEntry): TimetableSlot {
  return {
    id: String(entry.id),
    day: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'][entry.day_of_week] || 'Monday',
    startTime: entry.start_time,
    endTime: entry.end_time,
    subject: entry.subject || '—',
    room: entry.room || '',
    type: lessonTypeToSlotType(entry.lesson_type),
    teacherName: entry.teacher_name || null,
  };
}

interface Props {
  classId: number;
  gradebookConfigId: number | null;
  periodIndex: number;
  onOpenClassSchedule?: () => void;
}

const StudentOrarPanel: React.FC<Props> = ({ classId, gradebookConfigId, periodIndex, onOpenClassSchedule }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timetables, setTimetables] = useState<TimetableInfo[]>([]);
  const [selectedTimetableId, setSelectedTimetableId] = useState<number | null>(null);
  const [entries, setEntries] = useState<TimetableEntry[]>([]);

  const slots = useMemo(() => entries.map(entryToSlot), [entries]);

  const selectedTimetable = useMemo(
    () => timetables.find((t) => Number(t.id) === Number(selectedTimetableId)) || null,
    [selectedTimetableId, timetables]
  );

  const loadTimetables = async () => {
    if (!gradebookConfigId) {
      setTimetables([]);
      setSelectedTimetableId(null);
      setEntries([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get(`/api/orar/student/classrooms/${classId}/timetables`, {
        params: { gradebook_config_id: gradebookConfigId },
      });
      const list = Array.isArray(resp.data) ? (resp.data as TimetableInfo[]) : [];
      const ordered = list.slice().sort((a, b) => Number(a.period_index ?? 0) - Number(b.period_index ?? 0));
      setTimetables(ordered);
      const byPeriod = ordered.find((t) => Number(t.period_index) === Number(periodIndex)) || ordered[0] || null;
      setSelectedTimetableId(byPeriod?.id ?? null);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Nu s-a putut incarca lista de orare.');
      setTimetables([]);
      setSelectedTimetableId(null);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const loadEntries = async (ttId: number) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get(`/api/orar/student/timetables/${ttId}/entries`);
      setEntries(Array.isArray(resp.data) ? (resp.data as TimetableEntry[]) : []);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Nu s-a putut incarca orarul.');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadTimetables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, gradebookConfigId, periodIndex]);

  useEffect(() => {
    if (!selectedTimetableId) {
      setEntries([]);
      return;
    }
    void loadEntries(selectedTimetableId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimetableId]);

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Orar</h2>
          <div className="text-xs text-muted-foreground mt-1">
            {selectedTimetable ? selectedTimetable.name : 'Selecteaza un catalog pentru a vedea orarul.'}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          <Button
            variant="outline"
            size="sm"
            className="w-full sm:w-auto"
            iconName="RefreshCcw"
            iconPosition="left"
            onClick={loadTimetables}
            disabled={loading}
          >
            Reincarca
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="w-full sm:w-auto"
            iconName="ExternalLink"
            iconPosition="left"
            onClick={onOpenClassSchedule}
            disabled={!onOpenClassSchedule}
          >
            <span className="sm:hidden">Deschide clasa</span>
            <span className="hidden sm:inline">Deschide pagina clasei</span>
          </Button>
        </div>
      </div>

      {error && <div className="mb-4 bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{error}</div>}

      {loading ? (
        <div className="text-sm text-muted-foreground">Se incarca orarul...</div>
      ) : !gradebookConfigId ? (
        <div className="text-sm text-muted-foreground">Selecteaza un catalog pentru a vedea orarul.</div>
      ) : timetables.length === 0 ? (
        <div className="text-sm text-muted-foreground">Nu exista un orar pentru catalogul selectat.</div>
      ) : (
        <div className="space-y-4">
          <div className="sm:hidden flex items-center gap-2">
            <Icon name="AlignJustify" size={18} className="text-muted-foreground flex-shrink-0" />
            <select
              value={selectedTimetableId ?? ''}
              onChange={(e) => setSelectedTimetableId(e.target.value ? Number(e.target.value) : null)}
              className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={loading || timetables.length === 0}
            >
              {timetables.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          <div className="hidden sm:flex items-center gap-2 flex-wrap">
            {timetables.map((t) => {
              const active = Number(t.id) === Number(selectedTimetableId);
              return (
                <button
                  key={t.id}
                  onClick={() => setSelectedTimetableId(t.id)}
                  className={`px-3 py-1.5 rounded-md text-sm border transition-colors duration-150 ${
                    active ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border hover:bg-muted'
                  }`}
                  type="button"
                  disabled={loading}
                >
                  {t.name}
                </button>
              );
            })}
          </div>

          <TimetableView
            slots={slots}
            editable={false}
            onEditClick={undefined}
            onSlotClick={() => null}
          />

          {slots.length === 0 && (
            <div className="flex items-start gap-2 text-sm text-muted-foreground">
              <Icon name="Info" size={16} className="mt-0.5" />
              <div>Nu exista lectii completate in acest orar.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StudentOrarPanel;
